import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core"
import { ViraCard, InputStatusRow } from "@ksana/vira-ui"
import type { InputStatusItem } from "@ksana/vira-ui"
import clsx from "clsx"
import {
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  MouseEvent as ReactMouseEvent,
  useState,
} from "react"
import { formatDateTime } from "../../../utils/dateTimeUtils"
import { colors } from "../../../utils/theme"
import { useSession } from "../../../hooks/useSession"
import TranscriptCapturePanel from "./TranscriptCapturePanel"
import TranscriptSessionDetail, {
  TranscriptDetailTab,
} from "./TranscriptSessionDetail"
import TranscriptEditSessionDialog from "./TranscriptEditSessionDialog"
import TranscriptSessionList, {
  type SessionInputSummary,
} from "./TranscriptSessionList"
import {
  createManualTranscriptSession,
  createTranscriptUploadSession,
  attachTranscriptUploadToSession,
  deleteTranscriptSession,
  formatDefaultTranscriptTitle,
  fetchCarePlan,
  fetchProgressNote,
  fetchTranscriptSessionDetail,
  fetchTranscriptSessions,
  updateTranscriptSession,
  TranscriptSessionDetail as TranscriptSessionDetailData,
  TranscriptSessionSource,
  TranscriptSessionSummary,
} from "../../../utils/transcriptApi"

interface TranscriptSessionsProps {
  clientId: string
  practitionerId: string
  clientName: string
  practitionerName?: string
  onRequestStartRecording?: (event: ReactMouseEvent<HTMLElement>) => void
  /** Record into an existing empty session (Transcript tab). */
  onRequestRecordForSession?: (
    sessionId: string,
    event: ReactMouseEvent<HTMLElement>
  ) => void
  onPauseRecording?: () => void
  onResumeRecording?: () => void
  onStopRecording?: () => void
  isRecording?: boolean
  isPaused?: boolean
  isUploading?: boolean
  isProcessing?: boolean
  audioMeterFillRef?: MutableRefObject<HTMLDivElement | null>
  registerElapsedTimeElement?: (element: HTMLElement) => () => void
  recorderErrorMessage?: string | null
  refreshRequestId?: number
  preferredSessionId?: string | null
  /** Deep-link / controlled detail tab (Scores, Transcript, Action plan, Progress note). */
  preferredDetailTab?: TranscriptDetailTab | null
  /**
   * Fired when session and/or detail tab change (list click, tab click, or
   * auto-select). Used to sync `?session=` / `?tab=` on Client Detail.
   */
  onPastSessionsStateChange?: (next: {
    sessionId: string | null
    detailTab: TranscriptDetailTab
  }) => void
  onVisibleErrorMessageChange?: (errorMessage: string | null) => void
  onEditProgressNote?: (sessionId: string) => void
  /**
   * Client Detail: Action plan tab for the selected session.
   * `openProgressNote` switches to the Progress note tab (e.g. Draft the note).
   */
  renderActionPlan?: (
    sessionId: string,
    helpers: {
      openProgressNote: () => void
      hideInputs: boolean
      onInputsChange: (status: {
        items: InputStatusItem[]
        readyCount: number
        totalCount: number
      }) => void
      progressNoteGate: {
        status: "draft" | "signed" | null
        hasContent: boolean
      } | null
      requestRecordForSession: (event: ReactMouseEvent<HTMLElement>) => void
      requestUploadForSession: (file: File) => void
    }
  ) => ReactNode
  /** Client Detail: Progress note tab editor for the selected session. */
  renderProgressNote?: (
    sessionId: string,
    helpers: {
      onNoteGateChange: (gate: {
        status: "draft" | "signed" | null
        hasContent: boolean
      }) => void
    }
  ) => ReactNode
  /** Open on Progress Note when a preferred session is selected. */
  openOnProgressNote?: boolean
  /**
   * `full` — capture + history (legacy / non–Client Detail).
   * `capture` — New session pane (recorder + session details only).
   * `history` — Past sessions pane (list + detail only).
   */
  paneMode?: "full" | "capture" | "history"
  /** Client Detail past sessions — match ViraCard chrome; page title comes from ClientDetailPage. */
  variant?: "default" | "clientDetail"
}

/**
 * Practitioner transcript workspace scoped to the active client.
 */
export default function TranscriptSessions({
  clientId,
  practitionerId,
  clientName,
  practitionerName,
  onRequestStartRecording,
  onRequestRecordForSession,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
  isRecording = false,
  isPaused = false,
  isUploading = false,
  isProcessing = false,
  audioMeterFillRef,
  registerElapsedTimeElement,
  recorderErrorMessage,
  refreshRequestId,
  preferredSessionId,
  preferredDetailTab = null,
  onPastSessionsStateChange,
  onVisibleErrorMessageChange,
  onEditProgressNote,
  renderActionPlan,
  renderProgressNote,
  openOnProgressNote = false,
  paneMode = "full",
  variant = "default",
}: TranscriptSessionsProps) {
  const classes = useStyles()
  const isClientDetail = variant === "clientDetail"
  const { session } = useSession()
  const jwt = session?.jwt || ""

  const [sessions, setSessions] = useState<TranscriptSessionSummary[]>([])
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  )
  const [selectedSession, setSelectedSession] =
    useState<TranscriptSessionDetailData | null>(null)
  const [detailTab, setDetailTab] = useState<TranscriptDetailTab>(
    () => preferredDetailTab ?? "actionPlan"
  )
  const [sessionInputs, setSessionInputs] = useState<{
    items: InputStatusItem[]
    readyCount: number
    totalCount: number
  } | null>(null)
  const [progressNoteGate, setProgressNoteGate] = useState<{
    status: "draft" | "signed" | null
    hasContent: boolean
  } | null>(null)
  const [inputSummaries, setInputSummaries] = useState<
    Record<string, SessionInputSummary>
  >({})
  const [activeSegment, setActiveSegment] = useState<number | null>(null)
  const [sessionsLoading, setSessionsLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [isFileUploading, setIsFileUploading] = useState(false)
  const [isCreatingEmptySession, setIsCreatingEmptySession] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)
  const [renameTitle, setRenameTitle] = useState("")
  const [renameStartedAtLocal, setRenameStartedAtLocal] = useState("")
  const [renameErrorMessage, setRenameErrorMessage] = useState<string | null>(
    null
  )
  const [isRenaming, setIsRenaming] = useState(false)
  const [sessionListCollapsed, setSessionListCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return false
    }
    try {
      return window.localStorage.getItem("vira-session-list-collapsed") === "1"
    } catch {
      return false
    }
  })
  const preferredSessionIdRef = useRef(preferredSessionId)
  preferredSessionIdRef.current = preferredSessionId

  const hasProcessingSessions = useMemo(
    () =>
      sessions.some((session) =>
        ["uploading", "transcribing", "scoring"].includes(session.status)
      ) ||
      Boolean(
        selectedSession &&
          ["uploading", "transcribing", "scoring"].includes(
            selectedSession.session.status
          )
      ),
    [selectedSession, sessions]
  )

  useEffect(() => {
    let ignore = false

    const loadSessions = async () => {
      try {
        if (!ignore) {
          setSessionsLoading(true)
        }
        const nextSessions = await fetchTranscriptSessions(jwt, {
          clientId,
          practitionerId,
        })
        if (ignore) {
          return
        }
        setSessions(nextSessions)
        setSelectedSessionId((current) => {
          const preferred = preferredSessionIdRef.current
          if (
            preferred &&
            nextSessions.some((session) => session.id === preferred)
          ) {
            return preferred
          }
          if (
            current &&
            nextSessions.some((session) => session.id === current)
          ) {
            return current
          }
          return nextSessions[0]?.id ?? null
        })
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            getErrorMessage(error, "Unable to load transcript sessions.")
          )
        }
      } finally {
        if (!ignore) {
          setSessionsLoading(false)
        }
      }
    }

    void loadSessions()
    return () => {
      ignore = true
    }
  }, [clientId, jwt, practitionerId])

  useEffect(() => {
    if (sessionsLoading) {
      return
    }
    onPastSessionsStateChange?.({
      sessionId: selectedSessionId,
      detailTab,
    })
  }, [detailTab, onPastSessionsStateChange, selectedSessionId, sessionsLoading])

  useEffect(() => {
    if (!hasProcessingSessions) {
      return undefined
    }

    let ignore = false
    const intervalId = window.setInterval(async () => {
      try {
        const nextSessions = await fetchTranscriptSessions(jwt, {
          clientId,
          practitionerId,
        })
        if (!ignore) {
          setSessions(nextSessions)
          if (selectedSessionId) {
            const detail = await fetchTranscriptSessionDetail(
              jwt,
              selectedSessionId
            )
            if (!ignore) {
              setSelectedSession(detail)
            }
          }
        }
      } catch {
        return
      }
    }, 3000)

    return () => {
      ignore = true
      window.clearInterval(intervalId)
    }
  }, [clientId, hasProcessingSessions, jwt, practitionerId, selectedSessionId])

  useEffect(() => {
    if (!selectedSessionId) {
      setSelectedSession(null)
      setActiveSegment(null)
      setDetailTab("actionPlan")
      return undefined
    }

    setActiveSegment(null)

    let ignore = false

    const loadSelectedSession = async () => {
      try {
        if (!ignore) {
          setDetailLoading(true)
        }
        const detail = await fetchTranscriptSessionDetail(
          jwt,
          selectedSessionId
        )
        if (!ignore) {
          setSelectedSession(detail)
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            getErrorMessage(error, "Unable to load transcript session detail.")
          )
        }
      } finally {
        if (!ignore) {
          setDetailLoading(false)
        }
      }
    }

    void loadSelectedSession()
    return () => {
      ignore = true
    }
  }, [jwt, selectedSessionId])

  useEffect(() => {
    setSessionInputs(null)
    setProgressNoteGate(null)
  }, [selectedSessionId])

  const handleSessionInputsChange = useCallback(
    (status: {
      items: InputStatusItem[]
      readyCount: number
      totalCount: number
    }) => {
      setSessionInputs((current) => {
        if (
          current &&
          current.readyCount === status.readyCount &&
          current.totalCount === status.totalCount &&
          current.items.length === status.items.length &&
          current.items.every((item, index) => {
            const next = status.items[index]
            return (
              item.id === next.id &&
              item.status === next.status &&
              item.detail === next.detail &&
              item.title === next.title
            )
          })
        ) {
          return current
        }
        return status
      })
    },
    []
  )

  useEffect(() => {
    if (!jwt || sessions.length === 0) {
      setInputSummaries({})
      return
    }
    let ignore = false
    const loadSummaries = async () => {
      const entries = await Promise.all(
        sessions.map(async (session) => {
          const summary = await buildSessionInputSummary(jwt, session)
          return [session.id, summary] as const
        })
      )
      if (!ignore) {
        setInputSummaries(Object.fromEntries(entries))
      }
    }
    void loadSummaries()
    return () => {
      ignore = true
    }
  }, [jwt, sessions])

  useEffect(() => {
    if (!selectedSessionId || !sessionInputs) {
      return
    }
    setInputSummaries((current) => {
      const existing = current[selectedSessionId]
      const next = summaryFromInputItems(
        sessionInputs.items,
        sessionInputs.readyCount,
        sessionInputs.totalCount
      )
      if (
        existing &&
        existing.readyCount === next.readyCount &&
        existing.totalCount === next.totalCount &&
        existing.dots.length === next.dots.length &&
        existing.dots.every((dot, index) => {
          const other = next.dots[index]
          return (
            dot.id === other.id &&
            dot.status === other.status &&
            dot.detail === other.detail &&
            dot.label === other.label
          )
        })
      ) {
        return current
      }
      return {
        ...current,
        [selectedSessionId]: next,
      }
    })
  }, [selectedSessionId, sessionInputs])

  const refreshSessionsAndSelect = useCallback(
    async (sessionId: string) => {
      const nextSessions = await fetchTranscriptSessions(jwt, {
        clientId,
        practitionerId,
      })
      setSessions(nextSessions)
      setSelectedSessionId(sessionId)
    },
    [clientId, jwt, practitionerId]
  )

  useEffect(() => {
    if (!refreshRequestId) {
      return
    }

    let ignore = false

    const refreshSessions = async () => {
      try {
        const nextSessions = await fetchTranscriptSessions(jwt, {
          clientId,
          practitionerId,
        })
        if (ignore) {
          return
        }

        setSessions(nextSessions)
        setSelectedSessionId((current) => {
          if (
            preferredSessionId &&
            nextSessions.some((session) => session.id === preferredSessionId)
          ) {
            return preferredSessionId
          }
          if (
            current &&
            nextSessions.some((session) => session.id === current)
          ) {
            return current
          }
          return nextSessions[0]?.id ?? null
        })
      } catch {
        return
      }
    }

    void refreshSessions()
    return () => {
      ignore = true
    }
  }, [clientId, jwt, preferredSessionId, practitionerId, refreshRequestId])

  useEffect(() => {
    if (!preferredSessionId) {
      return
    }
    setSelectedSessionId(preferredSessionId)
    if (openOnProgressNote) {
      setDetailTab("progressNote")
    }
  }, [openOnProgressNote, preferredSessionId])

  useEffect(() => {
    if (!preferredDetailTab) {
      return
    }
    setDetailTab(preferredDetailTab)
  }, [preferredDetailTab])

  async function handleCreateEmptySession() {
    setErrorMessage(null)
    setIsCreatingEmptySession(true)
    try {
      const nameParts = clientName.trim().split(/\s+/)
      const title = formatDefaultTranscriptTitle(
        nameParts[0],
        nameParts.slice(1).join(" ") || undefined,
        new Date()
      )
      const createdSession = await createManualTranscriptSession(jwt, {
        clientId,
        practitionerId,
        title,
      })
      setDetailTab("actionPlan")
      await refreshSessionsAndSelect(createdSession.sessionId)
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Unable to create a plan-only session.")
      )
    } finally {
      setIsCreatingEmptySession(false)
    }
  }

  async function handleFileSelected(file: File) {
    const extension = file.name.split(".").pop()?.toLowerCase()
    if (!extension || !["pdf", "docx"].includes(extension)) {
      setErrorMessage("Only PDF and DOCX transcript files are supported.")
      return
    }

    setErrorMessage(null)
    setIsFileUploading(true)

    try {
      const createdSession = await createTranscriptUploadSession(jwt, {
        file,
        clientId,
        practitionerId,
      })
      await refreshSessionsAndSelect(createdSession.sessionId)
    } catch (error) {
      setErrorMessage(getErrorMessage(error, "Unable to upload transcript."))
    } finally {
      setIsFileUploading(false)
    }
  }

  async function handleAttachUploadToSelectedSession(file: File) {
    if (!selectedSessionId) {
      return
    }
    const extension = file.name.split(".").pop()?.toLowerCase()
    if (!extension || !["pdf", "docx"].includes(extension)) {
      setErrorMessage("Only PDF and DOCX transcript files are supported.")
      return
    }

    const sessionId = selectedSessionId
    setErrorMessage(null)
    setIsFileUploading(true)
    setDetailTab("transcript")
    // Show processing UI immediately — detail does not auto-reload on status change.
    setSelectedSession((current) =>
      current && current.session.id === sessionId
        ? {
            ...current,
            session: {
              ...current.session,
              status: "uploading",
              errorMessage: null,
            },
            transcript: null,
            scoreResult: null,
          }
        : current
    )
    setSessions((current) =>
      current.map((session) =>
        session.id === sessionId
          ? { ...session, status: "uploading", errorMessage: null }
          : session
      )
    )

    try {
      await attachTranscriptUploadToSession(jwt, sessionId, file)
      await refreshSessionsAndSelect(sessionId)
      const detail = await fetchTranscriptSessionDetail(jwt, sessionId)
      setSelectedSession(detail)
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Unable to attach transcript to this session.")
      )
      try {
        const detail = await fetchTranscriptSessionDetail(jwt, sessionId)
        setSelectedSession(detail)
        await refreshSessionsAndSelect(sessionId)
      } catch {
        // Keep optimistic uploading state until the next poll/refresh.
      }
    } finally {
      setIsFileUploading(false)
    }
  }

  function handleRecordForSelectedSession(event: ReactMouseEvent<HTMLElement>) {
    if (!selectedSessionId) {
      return
    }
    if (onRequestRecordForSession) {
      onRequestRecordForSession(selectedSessionId, event)
      return
    }
    onRequestStartRecording?.(event)
  }

  async function handleDeleteSession(sessionId: string) {
    const confirmed = window.confirm("Delete this transcript session?")
    if (!confirmed) {
      return
    }

    try {
      await deleteTranscriptSession(jwt, sessionId)
      const nextSessions = sessions.filter(
        (session) => session.id !== sessionId
      )
      setSessions(nextSessions)
      setSelectedSessionId((current) => {
        if (current !== sessionId) {
          return current
        }
        return nextSessions[0]?.id ?? null
      })
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Unable to delete transcript session.")
      )
    }
  }

  function handleOpenRenameDialog() {
    if (!selectedSession) {
      return
    }

    setRenameTitle(selectedSession.session.title || "")
    setRenameStartedAtLocal(
      toDatetimeLocalValue(selectedSession.session.createdAt)
    )
    setRenameErrorMessage(null)
    setIsRenameDialogOpen(true)
  }

  function handleCloseRenameDialog() {
    if (!isRenaming) {
      setIsRenameDialogOpen(false)
    }
  }

  async function handleRenameSession() {
    const title = renameTitle.trim()
    if (!selectedSessionId || !title) {
      setRenameErrorMessage("Enter a session title.")
      return
    }
    if (!renameStartedAtLocal) {
      setRenameErrorMessage("Enter a session date and time.")
      return
    }
    const startedAtIso = fromDatetimeLocalValue(renameStartedAtLocal)
    if (!startedAtIso) {
      setRenameErrorMessage("Enter a valid session date and time.")
      return
    }

    setRenameErrorMessage(null)
    setIsRenaming(true)
    try {
      const updatedSession = await updateTranscriptSession(
        jwt,
        selectedSessionId,
        { title, startedAt: startedAtIso }
      )
      setSessions((current) =>
        current.map((session) =>
          session.id === updatedSession.id
            ? {
                ...session,
                title: updatedSession.title,
                createdAt: updatedSession.createdAt,
              }
            : session
        )
      )
      setSelectedSession((current) =>
        current
          ? {
              ...current,
              session: {
                ...current.session,
                title: updatedSession.title,
                createdAt: updatedSession.createdAt,
              },
            }
          : current
      )
      setIsRenameDialogOpen(false)
    } catch (error) {
      setRenameErrorMessage(
        getErrorMessage(error, "Unable to update transcript session.")
      )
    } finally {
      setIsRenaming(false)
    }
  }

  const selectedSummary =
    selectedSession?.session ??
    sessions.find((session) => session.id === selectedSessionId) ??
    null

  const visibleErrorMessage = recorderErrorMessage || errorMessage
  const showCapture = paneMode === "full" || paneMode === "capture"
  const showHistory = paneMode === "full" || paneMode === "history"
  const isCaptureOnly = paneMode === "capture"

  const handleSessionListCollapsedChange = useCallback((next: boolean) => {
    setSessionListCollapsed(next)
    try {
      window.localStorage.setItem(
        "vira-session-list-collapsed",
        next ? "1" : "0"
      )
    } catch {
      // ignore quota / private mode
    }
  }, [])

  useEffect(() => {
    onVisibleErrorMessageChange?.(visibleErrorMessage)
  }, [onVisibleErrorMessageChange, visibleErrorMessage])

  return (
    <div className={classes.root}>
      {showCapture ? (
        <RecorderShell
          isClientDetail={isClientDetail}
          className={classes.recorderCard}
        >
          {!isClientDetail ? (
            <div className={classes.sectionHeader}>
              <div>
                <Typography variant="h3" className={classes.sectionTitle}>
                  Transcript Sessions
                </Typography>
                <Typography variant="body2" className={classes.sectionSubtitle}>
                  Recordings and uploads stay on this client&apos;s chart.
                  Sessions from the companion app also appear here when linked
                  to this client.
                </Typography>
              </div>
            </div>
          ) : (
            <Typography
              variant="body2"
              className={classes.sectionSubtitleClientDetail}
            >
              Recordings and uploads stay on this client&apos;s chart. Sessions
              from the companion app also appear here when linked to this
              client.
            </Typography>
          )}

          {visibleErrorMessage && (
            <Box className={classes.errorBanner}>
              <Typography variant="body2">
                Error: {visibleErrorMessage}
              </Typography>
            </Box>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <TranscriptCapturePanel
                isRecording={isRecording}
                isPaused={isPaused}
                isUploading={isUploading}
                isProcessing={isProcessing}
                isFileUploading={isFileUploading}
                onRequestStartRecording={onRequestStartRecording}
                onPauseRecording={onPauseRecording}
                onResumeRecording={onResumeRecording}
                onStopRecording={onStopRecording}
                onFileSelected={handleFileSelected}
                audioMeterFillRef={audioMeterFillRef}
                registerElapsedTimeElement={registerElapsedTimeElement}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                className={clsx(
                  classes.metadataPanel,
                  isClientDetail && classes.metadataPanelClientDetail
                )}
                elevation={0}
              >
                <Typography variant="body1" className={classes.captureTitle}>
                  Session details
                </Typography>
                <div className={classes.metadataGrid}>
                  <MetadataField label="Client" value={clientName} />
                  {practitionerName && (
                    <MetadataField label="Clinician" value={practitionerName} />
                  )}
                  {selectedSummary && !isCaptureOnly ? (
                    <>
                      <MetadataField
                        label="Recorded"
                        value={formatDateTime(selectedSummary.createdAt)}
                      />
                      <MetadataField
                        label="Duration"
                        value={
                          typeof selectedSummary.durationSec === "number"
                            ? formatDuration(
                                Math.round(selectedSummary.durationSec)
                              )
                            : "—"
                        }
                      />
                      <MetadataField
                        label="Captured from"
                        value={formatSource(selectedSummary.source)}
                      />
                    </>
                  ) : selectedSummary &&
                    (isRecording || isUploading || isProcessing) ? (
                    <>
                      <MetadataField
                        label="Recorded"
                        value={formatDateTime(selectedSummary.createdAt)}
                      />
                      <MetadataField
                        label="Duration"
                        value={
                          typeof selectedSummary.durationSec === "number"
                            ? formatDuration(
                                Math.round(selectedSummary.durationSec)
                              )
                            : "—"
                        }
                      />
                      <MetadataField
                        label="Captured from"
                        value={formatSource(selectedSummary.source)}
                      />
                    </>
                  ) : (
                    <>
                      <MetadataField label="Recorded" value="—" />
                      <MetadataField label="Duration" value="—" />
                      <MetadataField label="Captured from" value="—" />
                    </>
                  )}
                </div>
              </Paper>
            </Grid>
          </Grid>
        </RecorderShell>
      ) : null}

      {showHistory ? (
        <>
          {!showCapture && visibleErrorMessage ? (
            <Box className={classes.errorBanner}>
              <Typography variant="body2">
                Error: {visibleErrorMessage}
              </Typography>
            </Box>
          ) : null}
          <div
            className={clsx(
              isClientDetail
                ? classes.historyLayout
                : classes.historyLayoutLegacy,
              isClientDetail &&
                sessionListCollapsed &&
                classes.historyLayoutCollapsed
            )}
          >
            <div
              className={
                isClientDetail
                  ? classes.historyListCol
                  : classes.historyListColLegacy
              }
            >
              <TranscriptSessionList
                sessions={sessions}
                selectedSessionId={selectedSessionId}
                isLoading={sessionsLoading}
                onSelectSession={setSelectedSessionId}
                onDeleteSession={handleDeleteSession}
                inputSummaries={inputSummaries}
                variant={variant}
                onCreateEmptySession={
                  isClientDetail && showHistory
                    ? handleCreateEmptySession
                    : undefined
                }
                isCreatingEmptySession={isCreatingEmptySession}
                collapsed={isClientDetail ? sessionListCollapsed : false}
                onCollapsedChange={
                  isClientDetail ? handleSessionListCollapsedChange : undefined
                }
              />
            </div>

            <div
              className={
                isClientDetail
                  ? classes.historyDetailCol
                  : classes.historyDetailColLegacy
              }
            >
              <TranscriptSessionDetail
                selectedSession={selectedSession}
                selectedSessionId={selectedSessionId}
                detailLoading={detailLoading}
                detailTab={detailTab}
                activeSegment={activeSegment}
                isRenaming={isRenaming}
                onOpenRename={handleOpenRenameDialog}
                onDetailTabChange={setDetailTab}
                onJumpToSegment={(segmentIndex) => {
                  setActiveSegment(segmentIndex)
                  setDetailTab("transcript")
                }}
                onEditProgressNote={onEditProgressNote}
                inputsBanner={
                  sessionInputs ? (
                    <InputStatusRow
                      items={sessionInputs.items}
                      readyCount={sessionInputs.readyCount}
                      totalCount={sessionInputs.totalCount}
                    />
                  ) : null
                }
                actionPlanContent={
                  renderActionPlan && selectedSessionId
                    ? renderActionPlan(selectedSessionId, {
                        openProgressNote: () => setDetailTab("progressNote"),
                        hideInputs: true,
                        onInputsChange: handleSessionInputsChange,
                        progressNoteGate,
                        requestRecordForSession: (event) => {
                          handleRecordForSelectedSession(event)
                        },
                        requestUploadForSession: (file) => {
                          void handleAttachUploadToSelectedSession(file)
                        },
                      })
                    : undefined
                }
                progressNoteContent={
                  renderProgressNote && selectedSessionId
                    ? renderProgressNote(selectedSessionId, {
                        onNoteGateChange: setProgressNoteGate,
                      })
                    : undefined
                }
                variant={variant}
                onUploadTranscriptForSession={
                  handleAttachUploadToSelectedSession
                }
                onRecordForSession={handleRecordForSelectedSession}
                isAttachingTranscript={isFileUploading}
                isRecordingBusy={isRecording || isUploading || isProcessing}
              />
            </div>
          </div>
        </>
      ) : null}

      <TranscriptEditSessionDialog
        isOpen={isRenameDialogOpen}
        title={renameTitle}
        startedAtLocal={renameStartedAtLocal}
        errorMessage={renameErrorMessage}
        isSaving={isRenaming}
        onClose={handleCloseRenameDialog}
        onTitleChange={setRenameTitle}
        onStartedAtLocalChange={setRenameStartedAtLocal}
        onSubmit={handleRenameSession}
      />
    </div>
  )
}

const MetadataField: FC<{ label: string; value: string }> = ({
  label,
  value,
}) => {
  const classes = useStyles()
  return (
    <div className={classes.metadataField}>
      <Typography variant="body2" className={classes.metadataLabel}>
        {label}
      </Typography>
      <Typography variant="body2" className={classes.metadataValue}>
        {value}
      </Typography>
    </div>
  )
}

/** `YYYY-MM-DDTHH:mm` in the clinician's local timezone for datetime-local inputs. */
function toDatetimeLocalValue(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ""
  }
  const pad = (value: number) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Convert datetime-local wall time to an ISO string with timezone offset. */
function fromDatetimeLocalValue(local: string): string | null {
  const date = new Date(local)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toISOString()
}

function formatSource(source: TranscriptSessionSource | null): string {
  if (source === "web_mic") {
    return "Computer microphone"
  }
  if (source === "upload") {
    return "Uploaded file"
  }
  if (source === "manual") {
    return "Created without recording"
  }
  if (source === "mobile_app") {
    return "Companion app"
  }
  if (source) {
    return source.replace(/_/g, " ")
  }
  return "Unknown"
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : fallback
}

function RecorderShell({
  isClientDetail,
  className,
  children,
}: {
  isClientDetail: boolean
  className: string
  children: ReactNode
}) {
  if (isClientDetail) {
    return (
      <ViraCard padded sx={{ maxWidth: "100%" }}>
        {children}
      </ViraCard>
    )
  }
  return (
    <Paper className={className} elevation={0}>
      {children}
    </Paper>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "grid",
      gap: theme.spacing(3),
    },
    recorderCard: {
      border: `1px solid ${colors.neutral.clay}`,
      borderRadius: 4,
      padding: theme.spacing(3),
      backgroundColor: colors.neutral.white,
    },
    sectionHeader: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    sectionTitle: {
      color: colors.neutral.denim,
      fontWeight: 700,
    },
    sectionSubtitle: {
      color: colors.neutral.asphalt,
      marginTop: theme.spacing(0.5),
    },
    sectionSubtitleClientDetail: {
      color: "#4A5A70",
      fontSize: 13,
      marginBottom: theme.spacing(2.5),
    },
    captureTitle: {
      fontWeight: 700,
      color: colors.neutral.denim,
      marginBottom: theme.spacing(1),
    },
    infoChip: {
      backgroundColor: colors.primary.cloud,
      color: colors.primary.midnight,
      maxWidth: 420,
    },
    errorBanner: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 4,
      backgroundColor: colors.secondary.blush,
      color: colors.secondary.crimson,
    },
    metadataPanel: {
      border: `1px solid ${colors.neutral.clay}`,
      borderRadius: 4,
      padding: theme.spacing(3),
      minHeight: "100%",
      backgroundColor: colors.primary.cloud_05,
    },
    metadataPanelClientDetail: {
      // Nested panel inside recorder ViraCard — soft inset, not a second card.
      border: "1px solid #DDE1EA",
      borderRadius: 6,
      backgroundColor: "#F1F2F6",
    },
    metadataGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: theme.spacing(2),
      alignItems: "start",
    },
    metadataField: {
      minWidth: 0,
    },
    metadataLabel: {
      color: colors.neutral.asphalt,
      marginBottom: theme.spacing(0.5),
    },
    metadataValue: {
      fontWeight: 700,
      color: colors.neutral.denim,
      overflowWrap: "break-word",
    },
    historyLayout: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(3),
      width: "100%",
      minWidth: 0,
      [theme.breakpoints.down("md")]: {
        flexDirection: "column",
      },
    },
    historyLayoutCollapsed: {
      gap: theme.spacing(2),
      [theme.breakpoints.down("md")]: {
        gap: theme.spacing(1.5),
      },
    },
    historyListCol: {
      flex: "0 0 320px",
      maxWidth: 360,
      minWidth: 0,
      transition:
        "flex-basis 160ms ease, max-width 160ms ease, width 160ms ease",
      "$historyLayoutCollapsed &": {
        flex: "0 0 88px",
        maxWidth: 88,
        width: 88,
      },
      [theme.breakpoints.down("md")]: {
        flex: "1 1 auto",
        maxWidth: "100%",
        width: "100%",
        "$historyLayoutCollapsed &": {
          flex: "0 0 auto",
          maxWidth: "100%",
          width: "100%",
        },
      },
    },
    historyDetailCol: {
      flex: "1 1 auto",
      minWidth: 0,
      width: "100%",
    },
    historyLayoutLegacy: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: theme.spacing(3),
      [theme.breakpoints.up("lg")]: {
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)",
      },
    },
    historyListColLegacy: {
      minWidth: 0,
    },
    historyDetailColLegacy: {
      minWidth: 0,
    },
  })
)

function summaryFromInputItems(
  items: InputStatusItem[],
  readyCount: number,
  totalCount: number
): SessionInputSummary {
  return {
    readyCount,
    totalCount,
    dots: items
      .filter((item) => item.status !== "disabled")
      .map((item) => ({
        id: item.id,
        label: item.title,
        status: item.status,
        detail: item.detail,
      })),
  }
}

/** True when the session has usable transcript text (not an empty manual shell). */
function transcriptDetailHasContent(
  detail: TranscriptSessionDetailData | null | undefined
): boolean {
  if (!detail?.transcript) {
    return false
  }
  if (detail.transcript.rawText?.trim()) {
    return true
  }
  return detail.transcript.segments.some((segment) =>
    Boolean(segment.text?.trim())
  )
}

async function buildSessionInputSummary(
  jwt: string,
  session: TranscriptSessionSummary
): Promise<SessionInputSummary> {
  const sessionComplete = session.status === "complete"
  const app = {
    id: "app",
    label: "App data",
    status: "missing" as const,
    detail: "No recent app data",
  }

  let hasTranscriptContent = false
  if (sessionComplete) {
    try {
      const detail = await fetchTranscriptSessionDetail(jwt, session.id)
      hasTranscriptContent = transcriptDetailHasContent(detail)
    } catch {
      // Fall back to source heuristic when detail cannot be loaded.
      hasTranscriptContent =
        session.source !== "manual" && session.source !== null
    }
  }

  const sessionDot = {
    id: "session",
    label: "Session record",
    status: hasTranscriptContent ? ("ready" as const) : ("missing" as const),
    detail: hasTranscriptContent
      ? "Transcript available"
      : "No recording or transcript yet",
  }

  let planStatus: string | null = null
  let hasPlan = false
  let noteStatus: "draft" | "signed" | null = null
  let hasNoteContent = false

  if (sessionComplete) {
    try {
      const plan = await fetchCarePlan(jwt, session.id)
      planStatus = plan.status
      hasPlan = Boolean(plan.plan)
    } catch {
      // Treat as no plan yet.
    }
    try {
      const note = await fetchProgressNote(jwt, session.id)
      noteStatus = note.status
      hasNoteContent = Boolean(note.soapText?.trim() || note.birpText?.trim())
    } catch {
      // Treat as no note yet.
    }
  }

  const planReady =
    hasPlan &&
    planStatus != null &&
    !["draft", "composing"].includes(planStatus)
  const planInProgress =
    !planReady &&
    (planStatus === "draft" ||
      planStatus === "composing" ||
      (hasPlan && Boolean(planStatus)))

  const plan = {
    id: "plan",
    label: "Plan",
    status: planReady
      ? ("ready" as const)
      : planInProgress
      ? ("in_progress" as const)
      : ("missing" as const),
    detail: planReady
      ? planStatus === "signed"
        ? "Signed · nudges scheduled"
        : planStatus ?? "Ready"
      : planInProgress
      ? planStatus ?? "Draft"
      : "Not started",
  }

  const note = {
    id: "note",
    label: "Note",
    status:
      noteStatus === "signed"
        ? ("ready" as const)
        : noteStatus === "draft" || hasNoteContent
        ? ("in_progress" as const)
        : ("missing" as const),
    detail:
      noteStatus === "signed"
        ? "Finalized"
        : noteStatus === "draft" || hasNoteContent
        ? "Draft in progress"
        : "Not drafted",
  }

  const dots = [app, sessionDot, plan, note]
  const readyCount = dots.filter((dot) => dot.status === "ready").length
  return { readyCount, totalCount: dots.length, dots }
}
